<template>
	<div
		class="tw text-sm"
		role="radiogroup"
		:aria-label="ariaLabel && !label ? ariaLabel : undefined"
		:aria-labelledby="label ? 'salutation_label' : undefined"
	>
		<div :class="{ 'flex items-center gap-4': displayModeHorizontal }">
			<p v-if="label" id="salutation_label" :class="{ 'mb-2': !displayModeHorizontal }">{{ label }}</p>
			<div
				:class="[
					innerClass,
					{
						'flex items-center gap-4': displayModeHorizontalRadiobuttons || displayModeHorizontal,
						'flex flex-wrap gap-2': chip,
						'flex flex-col gap-2': displayModeVertical,
					},
				]"
			>
				<slot />
			</div>
		</div>
		<HintText v-if="hint && isHintVisible" :hint-text="hint" no-indent />
		<ValidationText
			v-if="isValidationVisible"
			class="pt-2"
			:data-test-name="dataTestName"
			:error-message="requiredMessage"
			no-indent
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref, toRef, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import HintText from "../SharedForm/HintText.vue";
import ValidationText from "../SharedForm/ValidationText.vue";
import type { HTMLInputElementEvent, InputEventDetails } from "../../types/SharedInput.type";

const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		chip?: boolean;
		innerClass?: string;
		displayName?: string;
		hint?: string;
		hintPermanent?: boolean;
		modelValue?: string | null;
		name?: string;
		required?: boolean;
		forcedErrorMessage?: string;
		requiredErrorMessage?: string;
		label?: string;
		ariaLabel?: string;
		disableErrorMessages?: boolean;
		dataTestName?: string;
		displayModeVertical?: boolean;
		displayModeHorizontal?: boolean;
		displayModeHorizontalRadiobuttons?: boolean;
	}>(),
	{
		dataTestName: "",
	}
);

const emit = defineEmits<{
	(event: "update:modelValue", value: string | null): void;
	(e: "change-error-msg", event: HTMLInputElementEvent, details: InputEventDetails): void;
}>();

const checkedRadio = ref<string | null>(props.modelValue ? props.modelValue : null);

watchEffect(() => {
	emit("update:modelValue", checkedRadio.value || null);
});

watch(
	() => props.modelValue as string | null,
	(newValue) => {
		checkedRadio.value = newValue;
		updateFormValidity();
	}
);

const isValidationVisible = ref(!props.disableErrorMessages && !!props.forcedErrorMessage);

const updateFormValidity: () => boolean | void = inject("updateFormValidity", () => {});
const requiredMessage = computed(() => {
	if (!isValidationVisible.value) return "";
	const fieldName = props.displayName || props.name || t("shared.radio.defaultField");
	return (
		props.forcedErrorMessage || props.requiredErrorMessage || t("shared.radio.requiredError", { field: fieldName })
	);
});

const updateInputValidityState = (
	fieldErrorMessage: string | undefined,
	isValidationOnFieldVisible: boolean | undefined
) => {
	isValidationVisible.value =
		!props.disableErrorMessages && (!!props.forcedErrorMessage || !!isValidationOnFieldVisible);
};

provide("radioGroupName", props.name);
provide("isRadioGroupChip", props.chip);
provide("checkedRadio", checkedRadio);
provide("isRadioGroupRequired", toRef(props, "required"));
provide("forcedErrorMessage", props.forcedErrorMessage);
provide("updateInputValidityState", updateInputValidityState);
provide("updateCheckedRadio", (value: string) => {
	checkedRadio.value = value;
	updateFormValidity();
});

const isHintVisible = computed(() => props.hintPermanent || !isValidationVisible.value);

watch([isValidationVisible, requiredMessage], () => {
	emit("change-error-msg", event as HTMLInputElementEvent, { errorMessage: requiredMessage.value });
});
</script>
